// src/services/topicLibraryService.js
//
// Read/write access to `topicLibrary/{slug}` — the canonical topic collection —
// and to `mediaLibrary`, the index of what already sits in Storage.
//
// WHAT THE LIBRARY OWNS
// ---------------------
// title, description (display default only), videoUrl, musicUrl, imageUrl,
// singAlongUrl, lyrics, gameType, bonusRoundType, subject.
//
// singAlongUrl is the sing-along MP4 behind a topic's 🎤 Sing-along button.
// Set by hand in the Topic Library, or automatically when the Sing-along
// studio saves a video (attachSingAlongVideo below). Empty = no button.
//
// WHAT IT DOES NOT OWN, AND WHY
// -----------------------------
// standardNumber, order, visible, and the description a class actually reads
// stay on the layout entry in portalLayouts. `watercycle` is 4-ESS2-1 under
// NGSS and 3.7 in the VA SOL files — same topic, different framework, and a
// shared document cannot carry one number honestly. Same for description: it
// quotes each state's own standard text, so VA prose and an NGSS performance
// expectation are both correct.
//
// The practical rule: if two states could reasonably disagree about it and
// both be right, it belongs on the layout. If disagreeing means one of them is
// stale, it belongs here.

import {
  collection,
  doc,
  deleteDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { exactKey, keyCandidates } from "../utils/topicKey";

// Re-exported so panels can build tool keys from the same module they load
// topics from. See src/utils/topicKey.js for the rule.
export { exactKey, legacyKey, makeToolKey, fileKeyOf, libraryIdOf } from "../utils/topicKey";

export const SUBJECTS = ["math", "science", "socialstudies", "other"];

// A document id exactly as Firestore holds it. Trimmed, never lowercased or
// stripped: ids are case-sensitive, and this addresses a document that exists.
const docIdOf = (value) => String(value ?? "").trim();

export const SUBJECT_LABELS = {
  math: "Math",
  science: "Science",
  socialstudies: "Social Studies",
  other: "Other",
};

/**
 * Makes the slug for a NEW topic: letters and digits only. Matches the
 * normalisation UniversalQuizGame uses when comparing a topic slug to a quiz
 * slug, so a topic created here resolves against `quizzes/{slug}`.
 *
 * Use it ONLY to invent a slug. Never use it to find an existing topic's
 * document: seeded topics keep the slug their layouts use (earth'swater,
 * water-cycle), and stripping that lands on a different document — on a
 * different TOPIC when a twin exists. Existing topics are addressed by their
 * real id (topic.id).
 */
export const toSlug = (value = "") =>
  String(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const emptyLibraryTopic = () => ({
  slug: "",
  title: "",
  description: "",
  subject: "science",
  gameType: "multipleChoice",
  bonusRoundType: "diamondTap",
  videoUrl: "",
  musicUrl: "",
  imageUrl: "",
  singAlongUrl: "",
  lyrics: [],
  status: "draft",
});

/* ------------------------------------------------------------------ */
/* Topics                                                             */
/* ------------------------------------------------------------------ */

export const getAllLibraryTopics = async () => {
  const snap = await getDocs(collection(db, "topicLibrary"));

  return snap.docs
    // The document's own name LAST: some records carry an `id` field inside
    // them, and spreading it after would give two records the same id —
    // which made rows from an earlier search stick in the Topic Library
    // table, and edits land on the wrong record.
    .map((d) => ({ ...d.data(), id: d.id }))
    .sort((a, b) =>
      String(a.title || a.slug).localeCompare(String(b.title || b.slug))
    );
};

/** Exact id first; the stripped id only when no document has the exact one. */
export const getLibraryTopic = async (slug) => {
  for (const id of keyCandidates(slug)) {
    const snap = await getDoc(doc(db, "topicLibrary", id));
    if (snap.exists()) return { ...snap.data(), id: snap.id };
  }
  return null;
};

/**
 * Writes a topic. Merges, so a partial save cannot blank fields the form did
 * not include.
 *
 * `manualEdit: true` is stamped on every save from this UI. seed-topic-library
 * skips documents carrying it unless run with --force, so re-running the
 * seeder after a hand edit will not quietly revert the edit.
 *
 * An EXISTING topic (it has an `id`) is written back to that exact document
 * with its slug untouched. Only a NEW topic gets a slug made by toSlug. This
 * is what stops a save on earth'swater from landing on earthswater.
 */
export const saveLibraryTopic = async (topic) => {
  const existingId = docIdOf(topic.id);
  const slug = existingId ? exactKey(topic.slug || existingId) : toSlug(topic.slug);
  const id = existingId || slug;
  if (!id) throw new Error("A topic needs a slug.");

  // `id` is the document's name, not a field in it.
  const fields = { ...topic };
  delete fields.id;

  const payload = {
    ...fields,
    slug,
    subject: SUBJECTS.includes(topic.subject) ? topic.subject : "other",
    lyrics: Array.isArray(topic.lyrics) ? topic.lyrics : [],
    manualEdit: true,
    updatedAt: serverTimestamp(),
  };

  // Once a human has chosen, the seeder's suggestions are noise in the
  // document. Clearing them also clears the "needs a pick" badge in the panel.
  if (payload.videoUrl && payload.mediaOptions?.videoUrl) {
    delete payload.mediaOptions.videoUrl;
  }
  if (payload.musicUrl && payload.mediaOptions?.musicUrl) {
    delete payload.mediaOptions.musicUrl;
  }
  payload.needsMediaPick = (payload.needsMediaPick || []).filter(
    (f) => !payload[f]
  );

  await setDoc(doc(db, "topicLibrary", id), payload, { merge: true });

  // The portal page caches the whole library for the life of the page. Without
  // this, publishing a topic and then previewing it in the same tab shows the
  // pre-edit version, which reads as "the change did not save".
  clearLibraryCache();

  return { ...payload, id };
};

/**
 * Flips a topic between Draft and Live (published). Writes ONLY the status
 * (merge), so pressing the Publish / Move to draft button in the Topic
 * Library drawer can never save half-finished edits sitting in the form.
 * Stamps manualEdit, because it is a human decision the seeder must not undo.
 *
 * Pass the topic's real library id (topic.id). Resolves the status written.
 */
export const setLibraryTopicStatus = async (topicId, status) => {
  const id = docIdOf(topicId);
  if (!id) throw new Error("A topic needs a slug.");
  const next = status === "published" ? "published" : "draft";
  await setDoc(
    doc(db, "topicLibrary", id),
    { status: next, manualEdit: true, updatedAt: serverTimestamp() },
    { merge: true }
  );
  clearLibraryCache();
  return next;
};

/**
 * Points a topic's 🎤 Sing-along button at a video. Writes that ONE field
 * (merge), so it cannot disturb anything else on the topic, and deliberately
 * does not stamp manualEdit — it is not an edit to the topic's own content.
 * Pass "" to remove the button.
 *
 * Pass the topic's real library id (topic.id), not a tool key.
 */
export const attachSingAlongVideo = async (topicId, url) => {
  const id = docIdOf(topicId);
  if (!id) throw new Error("A topic needs a slug.");
  await setDoc(
    doc(db, "topicLibrary", id),
    { singAlongUrl: url || "", updatedAt: serverTimestamp() },
    { merge: true }
  );
  clearLibraryCache();
};

/**
 * Points a topic's Song (musicUrl) at a file — used by the Sing-along studio
 * to put a faded MP3 on the topic, or to switch back to the original. Writes
 * that ONE field (merge). Pages whose layout carries its own inline song keep
 * theirs: the layout wins over the library (see mergeTopicWithLibrary).
 *
 * Pass the topic's real library id (topic.id), not a tool key.
 */
export const attachTopicSong = async (topicId, url) => {
  const id = docIdOf(topicId);
  if (!id) throw new Error("A topic needs a slug.");
  if (!url) throw new Error("No song file given.");
  await setDoc(
    doc(db, "topicLibrary", id),
    { musicUrl: url, updatedAt: serverTimestamp() },
    { merge: true }
  );
  clearLibraryCache();
};

/**
 * Points a topic's Video button (videoUrl) at a file — used by the Lesson
 * video studio's "Use as this topic's video". Writes that ONE field (merge).
 * Pages whose layout carries its own inline video keep theirs (the layout
 * wins over the library, see mergeTopicWithLibrary).
 *
 * Pass the topic's real library id (topic.id), not a tool key.
 */
export const attachTopicVideo = async (topicId, url) => {
  const id = docIdOf(topicId);
  if (!id) throw new Error("A topic needs a slug.");
  if (!url) throw new Error("No video file given.");
  await setDoc(
    doc(db, "topicLibrary", id),
    { videoUrl: url, updatedAt: serverTimestamp() },
    { merge: true }
  );
  clearLibraryCache();
};

/** Pass the topic's real library id (topic.id). Deletes exactly that document. */
export const deleteLibraryTopic = async (topicId) => {
  const id = docIdOf(topicId);
  if (!id) throw new Error("A topic needs a slug.");
  await deleteDoc(doc(db, "topicLibrary", id));
  clearLibraryCache();
};

/* ------------------------------------------------------------------ */
/* Media                                                              */
/* ------------------------------------------------------------------ */
//
// There is no picker code here on purpose.
//
// src/components/builder/MediaPicker.js already searches `mediaLibrary` — the
// index functions/mediaIndex.js builds from videos/, music/ and music-math/ —
// and resolves the chosen file's storage path into a download URL. The panel
// uses that component directly. A second search implementation living here
// would drift from it the first time either one changed.

/* ------------------------------------------------------------------ */
/* Reference resolution                                               */
/* ------------------------------------------------------------------ */

/**
 * Merges a library topic under a layout entry.
 *
 * Direction matters and is the whole design in one function: the LAYOUT wins
 * on anything it actually sets, and the library only fills blanks. That is
 * what makes migration safe to do one page at a time — a layout entry that
 * still carries its own inline videoUrl keeps it, and a layout entry that was
 * emptied to become a reference picks the library's up.
 *
 * standardNumber, order and visible are never taken from the library, even
 * when the layout entry omits them, because a missing standard number is a
 * blank field rather than an invitation to show another state's.
 */
export const mergeTopicWithLibrary = (layoutTopic = {}, libraryTopic = null) => {
  if (!libraryTopic) return layoutTopic;

  const pick = (field) => {
    const own = layoutTopic[field];
    if (own !== undefined && own !== null && own !== "") return own;
    return libraryTopic[field] ?? "";
  };

  return {
    ...layoutTopic,
    title: pick("title"),
    description: pick("description"),
    gameType: pick("gameType"),
    bonusRoundType: pick("bonusRoundType"),
    imageUrl: pick("imageUrl"),
    // Short field names are read first: every hand-authored topics file uses
    // `video`/`music` while the builder and the canonical libraries use
    // `videoUrl`/`musicUrl`. Checking both here means the merge works on a
    // layout from either era without a re-seed.
    videoUrl: layoutTopic.videoUrl || layoutTopic.video || libraryTopic.videoUrl || "",
    musicUrl: layoutTopic.musicUrl || layoutTopic.music || libraryTopic.musicUrl || "",
    singAlongUrl: layoutTopic.singAlongUrl || libraryTopic.singAlongUrl || "",
    lyrics:
      Array.isArray(layoutTopic.lyrics) && layoutTopic.lyrics.length
        ? layoutTopic.lyrics
        : libraryTopic.lyrics || [],
    _fromLibrary: true,
  };
};

/**
 * One fetch of the whole library, cached for the life of the page.
 *
 * A portal page renders 15–25 topics. Fetching each library document
 * individually would turn one document read into two dozen, on every page
 * load, for every student. The library is a few hundred small documents, so
 * reading it once and indexing by slug is both cheaper and simpler.
 */
let _cache = null;
export const getLibraryIndex = async ({ force = false } = {}) => {
  if (_cache && !force) return _cache;

  const topics = await getAllLibraryTopics();
  _cache = topics.reduce((acc, t) => {
    // Only published topics resolve. Drafts exist so the seeder can land data
    // that gets reviewed before it can affect a single classroom.
    // Keyed by the exact slug, lowercased: DynamicPortalPage looks a layout
    // entry up by its own slug, lowercased, and earth'swater must find
    // earth'swater rather than its twin.
    if (t.status === "published") acc[exactKey(t.slug || t.id)] = t;
    return acc;
  }, {});

  return _cache;
};

export const clearLibraryCache = () => {
  _cache = null;
};
